import { useContext, useEffect, useState } from 'react'
import { enqueueSnackbar } from 'notistack'

import { Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'

import { AppContext } from '/providers/app-context'
import { notifyPasses, notifyPass } from '/providers/passes-api'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import CreditQuote from '/components/credit-quote'

const NodifySelectedDialog = ({ open, setOpen, template, selection, passes: legacyPasses, refresh }) => {

  const { client, apiSource, apiKey, processing, setProcessing } = useContext(AppContext)
  const [notification, setNotification] = useState("")
  const [limitedUpdates, setLimitedUpdates] = useState(false)
  const [selectionScope, setSelectionScope] = useState('loaded')
  const [creditQuote, setCreditQuote] = useState(null)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()
  const normalisedSelection = selection ?? { type: 'loaded', count: legacyPasses?.length ?? 0, mode: 'live', passes: legacyPasses ?? [] }
  const loadedPasses = normalisedSelection.type === 'allMatching'
    ? (Array.isArray(normalisedSelection.loadedPasses) ? normalisedSelection.loadedPasses : [])
    : (Array.isArray(normalisedSelection.passes) ? normalisedSelection.passes : [])
  const selectedCount = selectionScope === 'allMatching' ? normalisedSelection.count : loadedPasses.length
  const hasSelectionFilter = Array.isArray(normalisedSelection.query) && normalisedSelection.query.length > 0
  const insufficientCredits = creditQuote?.ok && !creditQuote.sufficient

  useEffect(() => {
    if (open) setSelectionScope(normalisedSelection.type === 'allMatching' && hasSelectionFilter ? 'allMatching' : 'loaded')
  }, [open, normalisedSelection.type, hasSelectionFilter])

  useEffect(() => {
    setLimitedUpdates(false)
    if (client.limitUpdates) { // Client is only permitted  a limite dnumnber of updates per 24 hours
      setLimitedUpdates(loadedPasses.some(pass => pass.update_counter >= client.updates_per_day))
    }
  }, [client, loadedPasses])


  if (!template) return null

  const doSyncPasses = async () => {
    close()
    setProcessing(true)
    enqueueSnackbar({ message: 'Notifying passes', variant: 'light' })

    if (selectionScope === 'allMatching' || selectedCount > 1) {
      const result = await notifyPasses({
        apiSource,
        apiKey,
        templateId: template.template_id,
        passes: loadedPasses,
        notification,
        mode: normalisedSelection.mode,
        selectionQuery: selectionScope === 'allMatching' ? normalisedSelection.query : undefined,
      })

      if (result.ok) {
        enqueueSnackbar({ message: 'Notifications queued', variant: 'success' })
      } else {
        showActionError(result.error, 'Notification failed')
      }
    } else {
      const result = await notifyPass({ apiSource, apiKey, passId: loadedPasses[0].pass_id, notification })

      if (result.ok) {
        enqueueSnackbar({ message: 'Notification sent', variant: 'success' })
      } else {
        showActionError(result.error, 'Notification failed')
      }
    }

    if (refresh) refresh()
    setProcessing(false)
    setNotification("")
  }

  const close = () => setOpen(false)

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Notify Selected Passes : {template.name}
        </DialogTitle>

      {limitedUpdates &&
        <DialogContent>
          <div style={{ width: 600 }} className='flex flex-col gap-5'>
            <div>One or more passes has been updated or notified recently.</div>

            <div>
              Since you are using Smartix signing certifiates, you are limited to one pass update per 24 hours.
            </div>

            <div>
              If you require more frequent pass updates / notifications then please register your own Apple and Google developer accounts
              and provide us with your own signing certificates.
            </div>
          </div>
        </DialogContent>
      }
      {!limitedUpdates &&
        <DialogContent>
          <div style={{ width: 600 }} className='flex flex-col gap-5'>
            <p>This will send a notification to {selectedCount.toLocaleString()} selected passes.</p>
            <CreditQuote templateId={template.template_id} eventType='notify' passCount={selectedCount} mode={normalisedSelection.mode} onChange={setCreditQuote} />

            {normalisedSelection.type === 'allMatching' && (
              <RadioGroup value={selectionScope} onChange={event => setSelectionScope(event.target.value)}>
                <FormControlLabel
                  value="allMatching"
                  control={<Radio />}
                  disabled={!hasSelectionFilter}
                  label={`All ${normalisedSelection.count.toLocaleString()} passes matching the current filters`}
                />
                <FormControlLabel
                  value="loaded"
                  control={<Radio />}
                  label={`Only the ${loadedPasses.length.toLocaleString()} loaded and checked passes`}
                />
              </RadioGroup>
            )}

            <TextField value={notification} label="Notification text" sx={{ width: 550 }}
              onChange={e => setNotification(e.target.value.substring(0, 255))}
              slotProps={{ inputLabel: { shrink: true } }}
              autoFocus
              type="search" />
          </div>
        </DialogContent>
      }

        <DialogActions>
          <div className="flex flex-row justify-between w-full p-5">
            <Button variant="outlined" onClick={close} disabled={processing}>Cancel</Button>
            <Button variant="contained" onClick={doSyncPasses} autoFocus disabled={processing || selectedCount === 0 || notification.length < 5 || limitedUpdates || !creditQuote || insufficientCredits}><SyncOutlinedIcon className="mr-5" />Notify Selected Passes</Button>
          </div>
        </DialogActions>
      </Dialog>
      <ActionErrorDialog title="Notification failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default NodifySelectedDialog
